/* =============================================================================
   VIEWS / ADMIN / DAY-CLOSE-STATE — Modul-State + Pure-Helpers (Date/Range)
   =============================================================================
   v0.76.0-beta / Session 70 / REF-5b Cut 1/4
   ----------------------------------------------------------------------------
   Shared State + Pure-Helpers für day-close-Modul. _state als ESM live
   binding exportiert — Sub-Module (render, zbon-action, archive-detail)
   mutieren Properties direkt.
============================================================================ */

import { todayISO, getRestaurantTimezone, DEFAULT_TIMEZONE } from '../../utils/helpers.js';

export const _state = {
  date:        null,           // Stichtag für Z-Bon (wird beim ersten Render auf todayISO() gesetzt)
  exportMode:  'day',          // 'day' | 'month' | 'year' | 'custom'
  exportFrom:  null,           // Custom-Range (nur exportMode==='custom')
  exportTo:    null
};

export function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { orders: [], settings: {} };
}

export function fmtDateDE(iso) {
  if (!iso) return '';
  // Phase 28 (S26): TZ-aware Format. `T12:00:00Z` als Mittagspunkt, dann mit
  // restaurant-timezone projizieren → keine Off-by-one-Falle an Tagesgrenze.
  const d = new Date(iso + 'T12:00:00Z');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', {
    weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
    timeZone: getRestaurantTimezone() || DEFAULT_TIMEZONE
  });
}

/** Parst ein ISO-Datum 'YYYY-MM-DD' in {year, month, day} ohne TZ-Ambiguität.
 *  Reine String-Operation — DST-immun, Browser-TZ-immun. */
export function parseIsoYMD(iso) {
  const [y, m, d] = (iso || '').split('-').map(Number);
  return { year: y || 0, month: (m || 1) - 1, day: d || 1 };
}

/** Resolves _state.exportMode + Stichtag in {from, to, label}.
 *  Phase 28 (S26): keine `new Date(iso + 'T00:00:00')`-Browser-TZ-Konstruktion
 *  mehr — alles über String-Operationen, damit Bediener in fremder TZ trotzdem
 *  korrekte Tagesgrenzen bekommen. */
export function resolveExportRange() {
  const base = _state.date || todayISO();
  const { year, month } = parseIsoYMD(base);
  if (_state.exportMode === 'day') {
    return { from: base, to: base, label: 'tag-' + base };
  }
  if (_state.exportMode === 'month') {
    // Letzter Tag des Monats über Tag-0-des-Folgemonats-Trick (UTC-Math, immun).
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const mm = String(month + 1).padStart(2, '0');
    const fromIso = `${year}-${mm}-01`;
    const toIso   = `${year}-${mm}-${String(lastDay).padStart(2, '0')}`;
    const monthLabels = ['januar','februar','maerz','april','mai','juni','juli','august','september','oktober','november','dezember'];
    return {
      from: fromIso,
      to:   toIso,
      label: 'monat-' + monthLabels[month] + '-' + year
    };
  }
  if (_state.exportMode === 'year') {
    return { from: year + '-01-01', to: year + '-12-31', label: 'jahr-' + year };
  }
  // custom
  const from = _state.exportFrom || base;
  const to   = _state.exportTo   || base;
  const safeFrom = from <= to ? from : to;
  const safeTo   = from <= to ? to   : from;
  return { from: safeFrom, to: safeTo, label: 'zeitraum-' + safeFrom + '_' + safeTo };
}

export function fmtRangeDE({ from, to }) {
  if (from === to) return fmtDateDE(from);
  return fmtDateDE(from) + ' – ' + fmtDateDE(to);
}

export function centsToEur(cents) {
  const v = (Number(cents) || 0) / 100;
  return v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/** Test-Hook: setzt das Modul-private _state-Object zurück.
 *  Erlaubt sauberen Cross-Test-Reset ohne Reflection. */
export function _resetDayCloseStateForTests() {
  _state.date = null;
  _state.exportMode = 'day';
  _state.exportFrom = null;
  _state.exportTo = null;
}
